/**
 * Created by Jerker Nord on 2018-06-28.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite} from "gsap";
import {IceIceYetiGameAssets} from "../../IceIceYetiGameAssets";
import {IceIceYetiGameConfig} from "../../IceIceYetiGameConfig";
import {IceIceYetiGameModuleConfig} from "../../IceIceYetiGameModuleConfig";
import {IceIceYetiParsedGameData} from "../../server/data/IceIceYetiParsedGameData";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";

export class IceIceYetiInitialWPView extends BaseView {

    private _layer:ScalableLayer;
    private _winMainTimeline:TimelineLite | null;
    private _onWinAnimationCompleteCallback:Function;
    //Bigger number = faster animation
    private static REEL_ANIMATION_DELAY:number = 1.50;
    private static WIN_FRAME_ANIMATIONS_OFFSET:PIXI.Point = new PIXI.Point(0, 0);
    private _winType:WinRatioType;
    private _isBigWin:boolean;
    private _singleWin:number;
    private _winFrameAnimations:TimelineSprite[];
    private _activeGameAreaAfterSpin:boolean[][];
    private _belowStakeWinRestriction:boolean;
    private _betWayWins: IBetWayWinData[];

    constructor() {
        super();
        this._layer = StageManager.getLayer(IceIceYetiGameConfig.instance.LAYERS.BET_LINES.name);
    }

    protected addEventListeners():void {
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onGameDataParsed(event.params[0]));
    }

    protected onGameDataParsed(data:IceIceYetiParsedGameData):void {
        this._isBigWin = data.isBigWin;
        this._singleWin = data.singleWin;
        this._winType = WinRatioType.TINY;
        this._activeGameAreaAfterSpin = data.activeGameAreaAfterSpin;
        if(data.currentWinRatioConfig) {
            this._winType = <WinRatioType>data.currentWinRatioConfig.type;
        }
        this._betWayWins = <IBetWayWinData[]><unknown>data.betWins;

        this._belowStakeWinRestriction = SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && data.singleWin <= data.playedBetValue;
    }

    protected initAnimations():void {
        this._layer.addChild(this);
        this._winFrameAnimations = [];
        this._winMainTimeline = new TimelineLite({paused : true, onComplete : () => this.onWinPresentationComplete()});
    }

    public addReelWinPresentation(reel:number, allWinSymsOnReel:boolean[]):void {
        const reelAnimation:TimelineLite = new TimelineLite();
        for(let i:number = 0; i < allWinSymsOnReel.length; i++) {
            if(allWinSymsOnReel[i] == true) {
                const animation:TimelineLite = new TimelineLite();

                let winFrameDuration : number = 0;
                if (!this._belowStakeWinRestriction) {
                    const winFrame:TimelineSprite = new TimelineSprite(GameResources.getTextures(IceIceYetiGameAssets.WIN_FRAME));
                    winFrame.blendMode = PIXI.BLEND_MODES.ADD;
                    this._winFrameAnimations.push(winFrame);
                    animation.add(() => winFrame.anchor.set(0.5, 0.5));
                    animation.add(() => winFrame.position.x = Helper.getSymbolPositions(reel, i)[0] + IceIceYetiInitialWPView.WIN_FRAME_ANIMATIONS_OFFSET.x);
                    animation.add(() => winFrame.position.y = Helper.getSymbolPositions(reel, i)[1] + IceIceYetiInitialWPView.WIN_FRAME_ANIMATIONS_OFFSET.y);
                    animation.add(() => this.addChild(winFrame));
                    animation.add(() => winFrame.show());
                    animation.add(winFrame.getAnimation());
                    winFrameDuration = winFrame.totalDuration;
                }

                animation.add(() => Reels.getSymbol(reel, i)!.changeState({
                    state : IceIceYetiGameConfig.instance.SYMBOL_STATES.win,
                    fadeInDuration : 0.1
                }), "-= " + (winFrameDuration));
                reelAnimation.add(animation, "-= " + (winFrameDuration));
            } else {
                if(this._activeGameAreaAfterSpin[reel][i] == true) {
                    Reels.getSymbol(reel, i)!.changeState({
                        state : IceIceYetiGameConfig.instance.SYMBOL_STATES.noWin,
                        fadeInDuration : 0.2
                    })
                }
            }
        }
        if(this._winMainTimeline) {
            if(reel == 0) {
                this._winMainTimeline.add(reelAnimation);
            } else {
                this._winMainTimeline.add(reelAnimation, "-= " + IceIceYetiInitialWPView.REEL_ANIMATION_DELAY);
            }
        }
    }

    private onWinPresentationComplete():void {
        this._onWinAnimationCompleteCallback();
    }

    public playWinPresentation(winPresCompleteCallback:Function, totalWin:number):void {
        this._onWinAnimationCompleteCallback = winPresCompleteCallback;
        let countUpDuration:number = 2;
        if(this._winType == WinRatioType.LARGE) {
            countUpDuration = 2.6;
        }
        if(this._winMainTimeline) {
            if(!this._isBigWin) {
                this._winMainTimeline.add(IceIceYetiGameModuleConfig.WinCountUpDisplay.getWinDisplay(this.calculateTotalWinWays(), 0, this._singleWin, countUpDuration, this._winType, false, this._belowStakeWinRestriction), 0);
            }
            this._winMainTimeline.add(() => SlotGame.winFieldController.showWinField(totalWin));
            this._winMainTimeline.play();
        }
    }

    private calculateTotalWinWays():number{
        let totalBeyWays:number = 0;
        for (let i:number=0;i<this._betWayWins.length;i++){
            totalBeyWays += this._betWayWins[i].numberOfCombinations;
        }
        return totalBeyWays;
    }

    public stopWinPresentation(isAborted:boolean):void {
        if(this._winMainTimeline) {
            if(isAborted) {
                if(this._winMainTimeline.isActive()) {
                    this._winMainTimeline.progress(1);
                    this._winMainTimeline.pause();
                    this._winMainTimeline.kill();
                }
            }
            //Cleanup winframe animations
            for(let i:number = 0; i < this._winFrameAnimations.length; i++) {
                this.removeChild(this._winFrameAnimations[i]);
            }
            this._winMainTimeline = null;
        }

        this._winMainTimeline = new TimelineLite({paused : true, onComplete : () => this.onWinPresentationComplete()});
        this._winFrameAnimations = [];

        if(!this._isBigWin) {
            IceIceYetiGameModuleConfig.WinCountUpDisplay.getEndAnimation(this._singleWin, this._winType, isAborted);
        }
        SlotGame.winFieldController.showWinField();
    }
}